import { useTranslation } from "react-i18next";

const CONTENT = {
  en: {
    eyebrow: "About AdmissionCrafts",
    title: "Find the right college — without the guesswork.",
    body: [
      "AdmissionCrafts is built for students across India who are staring at a marksheet and a big decision. Whatever competitive exam brought you here, we do the same one job — take your marks and hand you a personalised, honest shortlist of the colleges you can actually get.",
      "Our prediction engine reads the official past-year cutoff data for each exam we cover and groups colleges into three simple confidence bands — High Chance, Possible Chance and Low Chance / Reach — so within minutes you know which choices are safe, which are stretches worth attempting, and which are dreams.",
      "We are not a CAP cell, a coaching class, or an admission broker. We don't recommend, advertise or take money from any college. AdmissionCrafts is a one-time paid guidance tool by OriginCraft Consultancy — built so that no student misses out on a good college simply because they didn't know it was within reach.",
    ],
    highlightTitle: "Exams we're live for today",
    highlights: [
      "MHT-CET · Maharashtra engineering CAP",
      "NEET · MCC + Maharashtra State DME counselling",
    ],
    highlightFooter: "More exams (MHT-CET pharmacy, JEE-based CAP counselling, more state counsellings) are on the roadmap.",
  },
  mr: {
    eyebrow: "अ‍ॅडमिशनक्राफ्ट्स बद्दल",
    title: "अंदाज न करता योग्य कॉलेज शोधा.",
    body: [
      "अ‍ॅडमिशनक्राफ्ट्स हे India भरातील त्या विद्यार्थ्यांसाठी बनवले आहे जे मार्कशीट हातात घेऊन मोठा निर्णय घेण्याच्या क्षणी असतात. तुम्ही कोणतीही प्रवेश परीक्षा दिली असो — आम्ही एकच काम करतो: तुमचे गुण घेऊन तुम्हाला खरोखर मिळू शकणाऱ्या कॉलेजांची personalised, प्रामाणिक यादी देतो.",
      "आमचे प्रेडिक्शन इंजिन आम्ही cover करत असलेल्या प्रत्येक परीक्षेच्या official मागील वर्षांच्या cutoff data वरून चालते आणि कॉलेजांची तीन सोप्या गटांत मांडणी करते — High Chance, Possible Chance आणि Low Chance / Reach — त्यामुळे काही मिनिटांतच कोणते सुरक्षित, कोणते प्रयत्न करण्यासारखे आणि कोणते स्वप्नच आहेत ते कळते.",
      "आम्ही CAP सेल, कोचिंग क्लास किंवा admission broker नाही. आम्ही कोणत्याही कॉलेजची शिफारस, जाहिरात किंवा पैसे घेत नाही. अ‍ॅडमिशनक्राफ्ट्स हे OriginCraft Consultancy ने तयार केलेले एकवेळ-शुल्क मार्गदर्शन साधन आहे — फक्त हाच उद्देश आहे की कोणत्याही विद्यार्थ्याने माहितीअभावी चांगले कॉलेज सोडू नये.",
    ],
    highlightTitle: "आज ज्या परीक्षांसाठी आम्ही live आहोत",
    highlights: [
      "MHT-CET · महाराष्ट्र engineering CAP",
      "NEET · MCC + महाराष्ट्र State DME counselling",
    ],
    highlightFooter: "अजून परीक्षा (MHT-CET pharmacy, JEE-आधारित CAP counselling आणि इतर state counsellings) roadmap वर आहेत.",
  },
};

export default function AboutBanner({ variant = "landing" }) {
  // landing: white card on light bg.  dashboard: subtler inline.
  // Language is driven by the single global toggle in the Navbar
  // (react-i18next). Removed per-section local toggles for a single source
  // of truth — users change language once, every section follows.
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2) === "mr" ? "mr" : "en";

  const c = CONTENT[lang];
  const isLanding = variant === "landing";

  return (
    <section
      className={
        isLanding
          ? "w-full bg-white border-y border-neutral-200 paper-grain"
          : "bg-white rounded-2xl border border-neutral-200 shadow-sm"
      }
      data-testid="about-banner"
      id="about-us"
    >
      <div className={isLanding ? "w-full max-w-6xl mx-auto px-6 lg:px-10 py-12" : "px-5 sm:px-7 py-6"}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-3xl">
            <p className="text-[10px] tracking-[0.25em] uppercase text-amber-700 font-semibold" data-testid="about-eyebrow">
              {c.eyebrow}
            </p>
            <h2
              className={
                isLanding
                  ? "mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900"
                  : "mt-2 font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900"
              }
              data-testid="about-title"
            >
              {c.title}
            </h2>
          </div>
        </div>
        <div className="mt-5 space-y-3 text-slate-700 leading-relaxed text-[15px] sm:text-base" data-testid="about-body">
          {c.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* Live-exams highlight (Feb 2026 v2). H1 stays generic ("multiple
            exams"); the specific exam list lives here in a green callout
            so parents can quickly confirm "yes, my child's exam is
            supported" without scanning the paragraphs above. */}
        {c.highlights?.length > 0 && (
          <div
            className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5"
            data-testid="about-live-exams"
          >
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-800">
              {c.highlightTitle}
            </p>
            <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[13.5px] text-emerald-900">
              {c.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" aria-hidden="true" />
                  <span className="font-semibold">{h}</span>
                </li>
              ))}
            </ul>
            {c.highlightFooter && (
              <p className="mt-3 text-[11.5px] text-emerald-800/80 italic">{c.highlightFooter}</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
